import React, { useState, useEffect, useRef } from "react";
import {
    Box, TextField, Button, Typography,
    CircularProgress, Divider, IconButton, Tooltip, Chip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { Link } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useTablePagination, useAppMeta } from "hooks";
import { exportExcelFile } from "utils/exportExcelFile";
import { localizeDateTimeShort } from "utils/dateFormats";
import {
    useLazyGetSendOutPendingDeliveryListQuery,
    useLazyGetSendOutPendingDeliverySummaryQuery,
} from "store/services/sendOutApi";

const DAYS_PRESETS = [
    { label: "5 Days", value: "5" },
    { label: "10 Days", value: "10" },
    { label: "30 Days", value: "30" },
    { label: "All", value: "all" },
];

const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Pending Delivery Report" },
];

const SummaryCard = ({ label, value, isLoading }) => (
    <Box
        sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "primary.lighter",
            borderLeft: "4px solid",
            borderRight: "4px solid",
            borderColor: "primary.main",
            minWidth: 130,
            flexShrink: 0,
        }}
    >
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            {label}
        </Typography>
        {isLoading ? (
            <CircularProgress size={24} />
        ) : (
            <Typography variant="h3" fontWeight="400" color="primary.dark">
                {value ?? 0}
            </Typography>
        )}
    </Box>
);

const DriverSendOutPendingDeliveryListReport = () => {
    useAppMeta("Pending Delivery Report");

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    // Keyword
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebounced] = useState("");
    const keywordTimerRef = useRef(null);

    // Days filter — free-text + presets, independent of date range
    const [maxDaysInput, setMaxDaysInput] = useState("5");
    const [debouncedMaxDays, setDebouncedMaxDays] = useState("5");
    const maxDaysTimerRef = useRef(null);

    // Date range — independent of days filter
    const [createdAtStart, setCreatedAtStart] = useState("");
    const [createdAtEnd, setCreatedAtEnd] = useState("");

    const [fetchList, { data: listData, isLoading, isFetching }] =
        useLazyGetSendOutPendingDeliveryListQuery();

    const [fetchExport] =
        useLazyGetSendOutPendingDeliveryListQuery();

    const [fetchSummary, { data: summaryData, isLoading: summaryLoading }] =
        useLazyGetSendOutPendingDeliverySummaryQuery();

    const buildParams = (extra = {}) => ({
        page: currentPage,
        limit: perPage,
        ...(debouncedKeyword && { keyword: debouncedKeyword }),
        ...(debouncedMaxDays && { maxDays: debouncedMaxDays }),
        ...(createdAtStart && { createdAtStart }),
        ...(createdAtEnd && { createdAtEnd }),
        ...extra,
    });

    useEffect(() => {
        fetchList(buildParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, debouncedKeyword, debouncedMaxDays, createdAtStart, createdAtEnd]);

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keyword — debounced 400ms
    const handleKeywordChange = (e) => {
        const val = e.target.value;
        setKeyword(val);
        clearTimeout(keywordTimerRef.current);
        keywordTimerRef.current = setTimeout(() => setDebounced(val), 400);
    };

    // Days input — fires immediately on each keystroke (debounced 300ms)
    const handleMaxDaysChange = (val) => {
        const resolved = val === "" ? "5" : val;
        setMaxDaysInput(resolved);
        clearTimeout(maxDaysTimerRef.current);
        maxDaysTimerRef.current = setTimeout(() => setDebouncedMaxDays(resolved), 300);
    };

    const handlePresetClick = (value) => {
        setMaxDaysInput(value);
        setDebouncedMaxDays(value);
    };

    const handleClearFilters = () => {
        setKeyword("");
        setDebounced("");
        setMaxDaysInput("");
        setDebouncedMaxDays("");
        setCreatedAtStart("");
        setCreatedAtEnd("");
    };

    const handleExport = async () => {
        try {
            const res = await fetchExport(
                buildParams({ page: undefined, limit: undefined })
            ).unwrap();
            const exportData = res?.data?.map((row) => ({
                "Shipment ID": row.shipmentId ?? "N/A",
                "Send Out ID": row.sendOutId ?? "N/A",
                "Send Out Date": row.sendOutCreatedAt
                    ? localizeDateTimeShort(row.sendOutCreatedAt) : "N/A",
                "Driver Name": row.driverName ?? "N/A",
                "Vehicle Registration": row.vehicleRegistration ?? "N/A",
                "Days Since Send Out": row.daysSinceSendOut ?? 0,
            }));
            exportExcelFile({
                sheetData: exportData,
                fileName: `Pending_Delivery_${createdAtStart || "All"}_to_${createdAtEnd || "now"}${maxDaysInput ? `_within_${maxDaysInput}_days` : ""}`,
            });
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row?.shipmentId,
            cell: (row) => (
                <Link
                    to={`/app/crm/shipment/${row?.shipmentObjectId}/detail`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.shipmentId
                        ? <HighlightedText text={row.shipmentId} highlight={debouncedKeyword} />
                        : "N/A"}
                </Link>
            ),
            minWidth: "150px",
        },
        {
            name: "Send Out ID",
            selector: (row) => row?.sendOutId,
            cell: (row) => (
                <Link
                    to={`/app/crm/sendOut/${row?._id}/detail`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.sendOutId
                        ? <HighlightedText text={row.sendOutId} highlight={debouncedKeyword} />
                        : "N/A"}
                </Link>
            ),
            minWidth: "150px",
        },
        {
            name: "Send Out Date",
            selector: (row) => row?.sendOutCreatedAt,
            cell: (row) => (
                <div className="text-xs">
                    {row?.sendOutCreatedAt
                        ? localizeDateTimeShort(row.sendOutCreatedAt)
                        : "N/A"}
                </div>
            ),
            minWidth: "160px",
        },
        {
            name: "Driver Name",
            selector: (row) => row?.driverName,
            cell: (row) => row?.driverName
                ? <HighlightedText text={row.driverName} highlight={debouncedKeyword} />
                : "N/A",
            minWidth: "140px",
        },
        {
            name: "Vehicle Registration",
            selector: (row) => row?.vehicleRegistration,
            cell: (row) => row?.vehicleRegistration
                ? <HighlightedText text={row.vehicleRegistration} highlight={debouncedKeyword} />
                : "N/A",
            minWidth: "170px",
        },
        {
            name: "Days Since Send Out",
            selector: (row) => row?.daysSinceSendOut,
            cell: (row) => <div>{row?.daysSinceSendOut ?? 0}</div>,
            minWidth: "75px",
            sortable: true,
        },
    ];

    const PRESET_VALUES = ["5", "10", "30", "all"];
    const hasFilters = !!(
        keyword ||
        (maxDaysInput && !PRESET_VALUES.includes(maxDaysInput)) ||
        createdAtStart ||
        createdAtEnd
    );

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        px: 2,
                        pt: 2,
                        pb: 1,
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    {/* Summary cards — left */}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <SummaryCard
                            label="Within 5 Days"
                            value={summaryData?.within5d}
                            isLoading={summaryLoading}
                        />
                        <SummaryCard
                            label="Within 10 Days"
                            value={summaryData?.within10d}
                            isLoading={summaryLoading}
                        />
                        <SummaryCard
                            label="Within 30 Days"
                            value={summaryData?.within30d}
                            isLoading={summaryLoading}
                        />
                    </Box>

                    {/* Filters — right */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            alignItems: "flex-end",
                            flexShrink: 0,
                        }}
                    >
                        {/* Row 1: Days filter */}
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                label="Days Since Send Out"
                                value={maxDaysInput}
                                onChange={(e) => handleMaxDaysChange(e.target.value)}
                                size="small"
                                placeholder='e.g. 3 or "all"'
                                sx={{ minWidth: 180 }}
                                inputProps={{ inputMode: "text" }}
                            />
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                {DAYS_PRESETS.map((p) => (
                                    <Chip
                                        key={p.value}
                                        label={p.label}
                                        size="small"
                                        onClick={() => handlePresetClick(p.value)}
                                        color={maxDaysInput === p.value ? "primary" : "default"}
                                        variant={maxDaysInput === p.value ? "filled" : "outlined"}
                                        clickable
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Row 2: Date range + keyword + clear + export */}
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                label="From"
                                type="date"
                                value={createdAtStart}
                                onChange={(e) => setCreatedAtStart(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                            <TextField
                                label="To"
                                type="date"
                                value={createdAtEnd}
                                onChange={(e) => setCreatedAtEnd(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                            <TextField
                                label="Search"
                                value={keyword}
                                onChange={handleKeywordChange}
                                size="small"
                                sx={{ minWidth: 200 }}
                                placeholder="Shipment, SendOut, Driver, Vehicle"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClearFilters}
                                disabled={!hasFilters}
                            >
                                Clear
                            </Button>
                            <Tooltip title="Export to Excel">
                                <span>
                                    <IconButton
                                        color="primary"
                                        onClick={handleExport}
                                        disabled={isLoading || isFetching || !listData?.data?.length}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                            borderRadius: 1,
                                            p: "6px",
                                            "&.Mui-disabled": { borderColor: "action.disabled" },
                                        }}
                                    >
                                        <Download fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                <Divider />

                <AppTable
                    columns={columns}
                    data={listData?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={listData?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default DriverSendOutPendingDeliveryListReport;